/**
 * @fileoverview Draw or edit ONE mission area with the existing DrawMap.
 *
 * DrawMap is the only piece of the legacy page that touches geometry, and
 * the only one kept: here it gets a single overlay (or none, for a new
 * area) instead of the whole plan, so it stays quick however big the plan
 * is. Saving goes through the same updateMission / createMissions calls
 * the legacy page used; the caller refreshes its slice afterwards.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import { Modal } from 'Components/Modal'
import { ModalBody } from 'Components/Modal/ModalBody'
import { ModalHeader } from 'Components/Modal/ModalHeader'
import { ModalTitle } from 'Components/Modal/ModalTitle'
import DrawMap from 'Components/DrawMap/container/DrawMap'
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator'
import { readGraphQLData } from 'Components/PowerBanks/hooks'
import {
  createMissions,
  fetchMissionBoundary,
  updateMissionNormalized,
} from 'Module/graphQL'
import { createOverlayName } from 'Utils/Map'
import { formatToLocalDate, getEndOfWeek } from 'shared/libraries/utils/date'
import { MISSION_ENUM_TYPES } from 'shared/constants'
import { getInitialOverlaysMapFromMissions } from '../util'

const MissionAreaEditorModal = ({
  show,
  mission,
  cityPlanId,
  campaignId,
  city,
  countryCode,
  onSaved,
  onClose,
}) => {
  const [boundary, setBoundary] = useState(null)
  const [loading, setLoading] = useState(false)

  // The list rows carry no polygon; fetch this one when editing.
  useEffect(() => {
    let cancelled = false
    if (!show || !mission) {
      setBoundary(null)
      return undefined
    }
    setLoading(true)
    ;(async () => {
      try {
        const data = await readGraphQLData(
          await fetchMissionBoundary(mission.id),
        )
        if (!cancelled)
          setBoundary((data.mission && data.mission.boundary) || [])
      } catch (error) {
        if (!cancelled) setBoundary([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [show, mission])

  const initialOverlays = useMemo(() => {
    if (!mission || !boundary || boundary.length === 0) return []
    return Object.values(
      getInitialOverlaysMapFromMissions([{ ...mission, boundary }], {
        isClient: false,
      }),
    )
  }, [mission, boundary])

  const onSave = useCallback(
    async ({ id, color, boundary: newBoundary, date, label, overlay }) => {
      const existingName =
        (overlay && overlay.payload && overlay.payload.label) ||
        (mission && mission.label)
      const name =
        label && label.length > 0
          ? label
          : existingName && existingName.length > 0
          ? existingName
          : await createOverlayName({
              countryCode,
              boundary: newBoundary,
              renderedOverlays: [],
            })
      if (overlay && overlay.set)
        overlay.set('payload', { ...overlay.payload, label: name })

      if (id) {
        const response = await updateMissionNormalized({
          missionInput: {
            id,
            boundary: newBoundary,
            boundaryColour: color,
            dateTime: date
              ? formatToLocalDate(getEndOfWeek(new Date(date)))
              : undefined,
            label: name,
            isNotShown: false,
          },
        })
        const result = await response.json()
        const updated = result.data.updateMission.mission
        onSaved(updated)
        return updated
      }

      const [created] = await createMissions({
        missions: [
          {
            cityPlanId: Number(cityPlanId),
            type: MISSION_ENUM_TYPES.LETTERBOX,
            dateTime: formatToLocalDate(getEndOfWeek(new Date())),
            boundary: newBoundary,
            boundaryColour: color,
            label: name,
          },
        ],
        campaignId,
      })
      onSaved(created)
      return created
    },
    [mission, countryCode, cityPlanId, campaignId, onSaved],
  )

  return (
    <Modal show={show} onHide={onClose} bsSize="large">
      <ModalHeader closeButton>
        <ModalTitle>
          {mission
            ? `Edit area: ${mission.label || mission.id}`
            : 'Draw a new area'}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <LoadingIndicator />
        ) : (
          <DrawMap
            initialOverlays={initialOverlays}
            onSave={onSave}
            onDelete={() => {}}
            mapOptions={{
              center: {
                lat: Number(city.latitude),
                lng: Number(city.longitude),
              },
            }}
            loading={false}
            hideMapControls={false}
            countryCode={countryCode}
            visibleOverlaysMap={mission ? { [mission.id]: true } : {}}
            mapControls={['DRAW']}
            skipHouseholdsDataOverview
          />
        )}
      </ModalBody>
    </Modal>
  )
}

MissionAreaEditorModal.propTypes = {
  show: PropTypes.bool.isRequired,
  mission: PropTypes.object,
  cityPlanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  city: PropTypes.object.isRequired,
  countryCode: PropTypes.string,
  onSaved: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}

MissionAreaEditorModal.defaultProps = {
  mission: null,
  countryCode: null,
}

export default MissionAreaEditorModal
